/**
 * @file Notice.tsx
 * @module app/components/Notice
 * @description One-time management notice modal for utrade-admin. Renders the latest entry
 * from the versioned notices list, locked (no backdrop/Esc dismiss) until the user acknowledges
 * via "Got it". Tracks acknowledgement per version using a cookie so a new notice version is
 * shown again even if an older version was already dismissed. Includes a local English/Swahili
 * toggle to read the message in either language, independent of the app-wide language setting.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.3.0
 * @version 1.3.0
 */

import { FC, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { CookieComponent } from '../../_admin/assets/ts/components';
import { getLatestNotice, NoticeLang } from '../utils/notice';

const NOTICE_COOKIE_NAME = 'utrade_notice_seen_v';
const NOTICE_COOKIE_EXPIRY_DAYS = 365;

const getSeenVersion = (): number => {
  const value = CookieComponent.get(NOTICE_COOKIE_NAME);
  return value ? parseInt(value, 10) || 0 : 0;
};

const markNoticeSeen = (version: number): void => {
  const expires = new Date();
  expires.setDate(expires.getDate() + NOTICE_COOKIE_EXPIRY_DAYS);
  CookieComponent.set(NOTICE_COOKIE_NAME, version, { expires });
};

const Notice: FC = () => {
  const notice = getLatestNotice();
  const [dismissed, setDismissed] = useState(
    !notice || getSeenVersion() >= notice.version
  );
  const [displayLang, setDisplayLang] = useState<NoticeLang>('sw');

  if (!notice || dismissed) {
    return null;
  }

  // Only closable via "Got it"; backdrop click / Esc are disabled below so the cookie is only set on explicit acknowledgement.
  const handleGotIt = () => {
    markNoticeSeen(notice.version);
    setDismissed(true);
  };

  const isSwahili = displayLang === 'sw';

  return (
    <Modal
      show
      onHide={() => {}}
      backdrop="static"
      keyboard={false}
      dialogClassName="modal-dialog-centered min-w-lg-500px"
      className="border-r10px"
    >
      <Modal.Header>
        <Modal.Title className="fs-20">
          {notice.title[displayLang]}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Content comes only from the developer-maintained notice.ts data file, not user/API input. */}
        <div
          className="fs-6 text-gray-700 mb-6"
          dangerouslySetInnerHTML={{ __html: notice.message[displayLang] }}
        />
      </Modal.Body>
      <Modal.Footer className="justify-content-between pt-1 border-top-0">
        <div className="d-flex align-items-center">
          <span
            className={`fs-7 me-2 ${!isSwahili ? 'fw-bold' : 'text-muted'}`}
          >
            EN
          </span>
          <label className="form-check form-switch form-check-custom form-check-solid">
            <input
              type="checkbox"
              className="form-check-input"
              checked={isSwahili}
              onChange={(e) => setDisplayLang(e.target.checked ? 'sw' : 'en')}
            />
          </label>
          <span className={`fs-7 ms-2 ${isSwahili ? 'fw-bold' : 'text-muted'}`}>
            SW
          </span>
        </div>
        <Button className='uw-btn uw-btn-primary' onClick={handleGotIt}>
          Got it
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export { Notice };
